import { useEffect, useRef, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { apiGet } from "@/utils/api";

export const SupplierLookup = ({ value, onChange }) => {
  const [query, setQuery] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [more, setMore] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [retry, setRetry] = useState(0);
  const container = useRef(null);
  useEffect(() => {
    const dismiss = (event) => { if (!container.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, []);
  useEffect(() => {
    if (value || query.trim().length < 2) { setLoading(false); return; }
    let active = true;
    const controller = new AbortController();
    let timeout;
    setLoading(true); setError("");
    const timer = setTimeout(() => {
      timeout = setTimeout(() => controller.abort(), 35000);
      apiGet(`/lookups/suppliers?q=${encodeURIComponent(query.trim())}`, { signal: controller.signal })
        .then((data) => { if (active) { setResults(data.items); setMore(data.has_more); } })
        .catch((err) => { if (active) { setError(err.name === "AbortError" ? "SAP supplier search timed out. Please retry." : err.message); setResults([]); } })
        .finally(() => { clearTimeout(timeout); if (active) setLoading(false); });
    }, 350);
    return () => { active = false; clearTimeout(timer); clearTimeout(timeout); controller.abort(); };
  }, [query, value, retry]);
  const choose = (supplier) => { onChange(supplier); setQuery(`${supplier.name} · ${supplier.id}`); setOpen(false); };
  const edit = (text) => { setQuery(text); onChange(null); setResults([]); setError(""); setMore(false); setActiveIndex(-1); setOpen(text.trim().length >= 2); };
  const onKeyDown = (event) => {
    if (event.key === "Escape") setOpen(false);
    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault(); setOpen(true);
      setActiveIndex((index) => Math.max(0, Math.min(results.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
    }
    if (event.key === "Enter" && !value) {
      event.preventDefault();
      if (open && results[activeIndex]) choose(results[activeIndex]);
    }
  };
  return <div ref={container} className="relative" data-testid="supplier-lookup">
    <div className="relative">
      <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <Input role="combobox" aria-label="Supplier or customs broker" aria-expanded={open} aria-controls="supplier-lookup-options" aria-autocomplete="list"
        aria-activedescendant={open && results[activeIndex] ? `supplier-option-${results[activeIndex].id}` : undefined}
        value={query} onChange={(event) => edit(event.target.value)} onKeyDown={onKeyDown}
        onFocus={() => !value && query.trim().length >= 2 && setOpen(true)} autoComplete="off"
        placeholder="Supplier name or vendor / business partner code" className="h-10 pl-9 pr-10 text-sm" data-testid="bp-search-input" />
      {query && <button type="button" aria-label="Clear supplier" onClick={() => edit("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" data-testid="clear-supplier-button"><X size={15} /></button>}
    </div>
    {open && <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto border border-slate-200 bg-white shadow-lg" data-testid="supplier-lookup-dropdown">
      <div className="border-b px-3 py-2 text-[11px] font-bold text-[#0A838A]" data-testid="supplier-lookup-source">SAP supplier master · Live</div>
      {loading ? <div className="flex items-center gap-2 p-3 text-sm text-slate-500" data-testid="supplier-lookup-loading"><Loader2 size={14} className="animate-spin" /> Searching SAP…</div> : error ? <p role="alert" className="p-3 text-sm text-red-700" data-testid="supplier-lookup-error">{error}</p> : !results.length ? <p className="p-3 text-sm text-slate-500" data-testid="supplier-lookup-empty">No active suppliers match this name or code.</p> : null}
      {!loading && error && <button type="button" onClick={() => setRetry((value) => value + 1)} className="px-3 pb-3 text-sm font-bold text-[#0A838A] hover:underline" data-testid="retry-supplier-lookup-button">Retry search</button>}
      <div id="supplier-lookup-options" role="listbox" aria-label="SAP suppliers">{!loading && results.map((supplier, index) => <button
        type="button" role="option" aria-selected={index === activeIndex} id={`supplier-option-${supplier.id}`} key={supplier.id}
        onClick={() => choose(supplier)} data-testid={`bp-option-${supplier.id}`}
        className={`block w-full break-words px-3 py-2 text-left transition-colors hover:bg-[#F0F9F8] ${index === activeIndex ? "bg-[#E7F5F5]" : ""}`}>
        <span className="block text-sm font-semibold text-slate-900">{supplier.name}</span><span className="font-mono text-xs text-slate-500">{supplier.id}</span>
      </button>)}</div>
      {!loading && more && <p className="border-t p-3 text-xs text-slate-500" data-testid="supplier-lookup-more">More matches available. Refine the name or code.</p>}
    </div>}
    {value && <p className="mt-1 text-xs text-slate-500" data-testid="selected-supplier-code">SAP supplier {value.id}</p>}
  </div>;
};